class AnnouncementBar extends HTMLElement {
  constructor() {
    super();

    this.currentIndex = 0;
    this.touchStartX = 0;
    this.touchEndX = 0;
    this.announcementBar = this.querySelector("#wt-announcement__container");
    this.announcementInterval = null;

    this.changeAnnouncement = this.changeAnnouncement.bind(this);
    this.previousAnnouncement = this.previousAnnouncement.bind(this);
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchMove = this.handleTouchMove.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);
    this.hideForOneDay = this.hideForOneDay.bind(this);
    this.restartInterval = this.restartInterval.bind(this);
    this.isMobileView = this.isMobileView.bind(this);
  }

  connectedCallback() {
    this.closeButton = this.querySelector(".wt-announcement__close");
    if (this.closeButton)
      this.closeButton.addEventListener("click", this.hideForOneDay);

    if (this.dataset.navMode === "arrows") {
      this.initArrows();
      return;
    }

    if (
      !this.announcementBar?.classList.contains(
        "wt-announcement__container--marquee",
      )
    ) {
      this.announcementInterval = setInterval(this.changeAnnouncement, 5000);

      if (this.announcementBar) {
        this.announcementBar.addEventListener(
          "touchstart",
          this.handleTouchStart,
          { passive: true },
        );
        this.announcementBar.addEventListener(
          "touchmove",
          this.handleTouchMove,
          {
            passive: true,
          },
        );
        this.announcementBar.addEventListener("touchend", this.handleTouchEnd, {
          passive: true,
        });
        this.announcementBar.addEventListener(
          "touchstart",
          () => clearInterval(this.announcementInterval),
          { passive: true },
        );
        this.announcementBar.addEventListener(
          "touchend",
          this.restartInterval,
          {
            passive: true,
          },
        );
      }
    }

    if (this.announcementBar)
      this.announcementBar.style.transition = "transform 0.5s ease-in-out";

    window.addEventListener("resize", this.handleResize.bind(this));
  }

  initArrows() {
    this.track = this.querySelector(".wt-announcement__track");
    this.slidesCount = this.track ? this.track.children.length : 0;
    this.autoplay = this.dataset.autoplay === "true";

    if (this.track && this.slidesCount > 1) {
      this.handleArrowTouchEnd = this.handleArrowTouchEnd.bind(this);
      this.handleLoopReset = this.handleLoopReset.bind(this);
      this.setupLoop();

      this.track.addEventListener("touchstart", this.handleTouchStart, {
        passive: true,
      });
      this.track.addEventListener("touchmove", this.handleTouchMove, {
        passive: true,
      });
      this.track.addEventListener("touchend", this.handleArrowTouchEnd, {
        passive: true,
      });
      this.track.addEventListener("transitionend", this.handleLoopReset);
    }

    this.querySelector(".wt-announcement__nav--prev")?.addEventListener(
      "click",
      () => this.moveArrow(-1),
    );
    this.querySelector(".wt-announcement__nav--next")?.addEventListener(
      "click",
      () => this.moveArrow(1),
    );

    if (this.autoplay && this.slidesCount > 1) {
      this.startArrowAutoplay();
      this.addEventListener("mouseenter", () =>
        clearInterval(this.announcementInterval),
      );
      this.addEventListener("mouseleave", () => this.startArrowAutoplay());
    }
  }

  // Clone the first slide to the end and the last slide to the start so the
  // track can loop seamlessly in either direction. Real slides then live at
  // positions 1..slidesCount inside the extended track.
  setupLoop() {
    const slides = Array.from(this.track.children);
    const firstClone = slides[0].cloneNode(true);
    const lastClone = slides[slides.length - 1].cloneNode(true);

    [firstClone, lastClone].forEach((clone) => {
      clone.setAttribute("aria-hidden", "true");
      clone
        .querySelectorAll("a")
        .forEach((link) => link.setAttribute("tabindex", "-1"));
    });

    this.track.appendChild(firstClone);
    this.track.insertBefore(lastClone, slides[0]);

    this.position = 1;
    this.setTrackPosition(false);
  }

  setTrackPosition(animate) {
    const isRtl = getComputedStyle(this.track).direction === "rtl";
    const sign = isRtl ? 1 : -1;

    if (!animate) this.track.style.transition = "none";
    this.track.style.transform = `translateX(${sign * this.position * 100}%)`;
    if (!animate) {
      // Flush the jump, then restore the CSS transition for future moves.
      void this.track.offsetWidth;
      this.track.style.transition = "";
    }
  }

  // After animating onto a clone, snap (without transition) to the matching
  // real slide. The clone is identical, so the swap is invisible.
  handleLoopReset(e) {
    if (e.target !== this.track || e.propertyName !== "transform") return;
    if (this.position === 0) {
      this.position = this.slidesCount;
      this.setTrackPosition(false);
    } else if (this.position === this.slidesCount + 1) {
      this.position = 1;
      this.setTrackPosition(false);
    }
  }

  startArrowAutoplay() {
    clearInterval(this.announcementInterval);
    this.announcementInterval = setInterval(() => this.moveArrow(1), 5000);
  }

  moveArrow(step) {
    if (!this.track || this.slidesCount <= 1) return;

    // If we're parked on a clone (e.g. rapid clicks before transitionend),
    // snap to the matching real slide first so the next step is continuous.
    if (this.position === 0) {
      this.position = this.slidesCount;
      this.setTrackPosition(false);
    } else if (this.position === this.slidesCount + 1) {
      this.position = 1;
      this.setTrackPosition(false);
    }

    this.position += step;
    this.setTrackPosition(true);
    if (this.autoplay) this.startArrowAutoplay();
  }

  handleArrowTouchEnd() {
    const swipeThreshold = 30;
    if (Math.abs(this.touchEndX - this.touchStartX) > swipeThreshold) {
      this.moveArrow(this.touchEndX < this.touchStartX ? 1 : -1);
    }
  }

  handleResize() {
    if (window.innerWidth >= 900) {
      // Reset the translateX to its initial state
      if (this.announcementBar) {
        this.announcementBar.style.transform = "translateX(0)";
        this.currentIndex = 0;
      }
    }
  }

  isMobileView() {
    return window.innerWidth < 900;
  }

  restartInterval() {
    clearInterval(this.announcementInterval);
    this.announcementInterval = setInterval(this.changeAnnouncement, 5000);
  }

  hideForOneDay() {
    const oneDayLater = new Date().getTime() + 24 * 60 * 60 * 1000;
    localStorage.setItem("wt-announcement-hidden", oneDayLater);
    this.style.display = "none";
  }

  changeAnnouncement() {
    if (!this.isMobileView()) {
      return;
    }

    const totalSlides = this.announcementBar?.children.length;

    // Move to the next slide
    this.currentIndex = (this.currentIndex + 1) % totalSlides;
    const newPosition = -(this.currentIndex * 100);

    if (this.announcementBar)
      this.announcementBar.style.transform = `translateX(${newPosition}vw)`;

    if (this.currentIndex === 0 && this.announcementBar) {
      // When we're back at the start, reset position with transition.
      setTimeout(() => {
        this.announcementBar.style.transition = "none";
        this.announcementBar.style.transform = "translateX(0)";
        this.currentIndex = 0;
        // Restore transition after resetting position
        setTimeout(() => {
          this.announcementBar.style.transition = "transform 0.5s ease-in-out";
        }, 0);
      }, 490); // Just slightly before 0.5s to ensure it happens before next slide movement.
    }
  }

  previousAnnouncement() {
    const totalSlides = this.announcementBar?.children.length;

    this.currentIndex = (this.currentIndex - 1 + totalSlides) % totalSlides;
    const newPosition = -(this.currentIndex * 100);
    if (this.announcementBar)
      this.announcementBar.style.transform = `translateX(${newPosition}vw)`;
  }

  handleTouchStart(e) {
    if (this.dataset.navMode === "arrows" || this.isMobileView()) {
      this.touchStartX = e.touches[0].clientX;
    }
  }

  handleTouchMove(e) {
    if (this.dataset.navMode === "arrows" || this.isMobileView()) {
      this.touchEndX = e.touches[0].clientX;
    }
  }

  handleTouchEnd() {
    const swipeThreshold = 30;

    if (Math.abs(this.touchEndX - this.touchStartX) > swipeThreshold) {
      if (this.touchEndX < this.touchStartX) {
        this.changeAnnouncement();
      } else {
        this.previousAnnouncement();
      }
    }
  }

  disconnectedCallback() {
    clearInterval(this.announcementInterval);
    this.announcementBar?.removeEventListener(
      "touchstart",
      this.handleTouchStart,
    );
    this.announcementBar?.removeEventListener("touchmove", this.handleTouchMove);
    this.announcementBar?.removeEventListener("touchend", this.handleTouchEnd);
    if (this.track) {
      this.track.removeEventListener("touchstart", this.handleTouchStart);
      this.track.removeEventListener("touchmove", this.handleTouchMove);
      this.track.removeEventListener("touchend", this.handleArrowTouchEnd);
      this.track.removeEventListener("transitionend", this.handleLoopReset);
    }
    window.removeEventListener("resize", this.handleResize.bind(this));
  }
}

customElements.define("announcement-bar", AnnouncementBar);
